import React, { useEffect, useState, useRef } from 'react';
import { Box, TextInput } from 'grommet';
import TagItem from './tag-item';
import { itemTypeNames } from '../items/item-types';
import { getSuggestions, getSuggestionItem } from '../items/items';
import { getItemNames } from '../utils';

const ItemTagSelector = ({
  itemType,
  itemTypes,
  itemIds = [],
  onItemAdd,
  onItemRemove,
  width = 'auto',
  disabled = false,
  filter,
  size = 'medium',
  externalSuggestions,
  canEdit = true,
  excludeArchived = false
}) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const textRef = useRef();

  // itemIds can grow from outside this component's own onSelect/onItemAdd -
  // e.g. Schedule Mode's projection folding in a scheduled Assigned
  // Creatives change - so this can't just be a mount-only fetch. Only the
  // ids not already resolved are looked up, and the result is merged into
  // itemNames rather than replacing it, so a resolved name is never lost.
  // itemIds can grow from outside this component's own onSelect/onItemAdd -
  // e.g. Schedule Mode's projection folding in a scheduled Assigned
  // Creatives change - so this can't just be a mount-only fetch. Only the
  // ids not already resolved are looked up, and the result is merged into
  // itemNames rather than replacing it, so a resolved name is never lost.
  useEffect(() => {
    const unresolvedIds = itemIds.filter(id => !(id in itemNames));
    if (unresolvedIds.length === 0) return;
    getItemNames(
      unresolvedIds,
      fetchedNames => setItemNames(prev => ({ ...prev, ...fetchedNames })),
      () => {
        console.log('err');
      },
      itemTypes ?? [itemType]
    );
    // itemNames is deliberately excluded - it's this effect's own output,
    // and including it would make each fetch immediately re-trigger itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemIds, itemType, itemTypes]);

  useEffect(() => {
    //can pass in suggestions instead of searching indices for them
    if (Array.isArray(externalSuggestions)) {
      setSuggestions(
        externalSuggestions
          .filter(
            s =>
              itemIds.indexOf(s.id) === -1 &&
              s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
          )
          .map(getSuggestionItem)
      );
    } else {
      getSuggestions(
        value,
        itemTypes ?? itemType,
        itemIds,
        setSuggestions,
        filter,
        excludeArchived
      );
    }
  }, [
    value,
    itemType,
    itemTypes,
    itemIds,
    filter,
    externalSuggestions,
    excludeArchived
  ]);

  return (
    <Box
      direction='row'
      border={{ color: 'border500' }}
      round='xsmall'
      style={{ width }}
    >
      <Box
        direction='row'
        wrap={true}
        style={{ maxWidth: '65%' }}
        align='center'
        flex={{ shrink: 0 }}
      >
        {itemIds.map(id => (
          <TagItem
            size={size}
            key={id}
            tag={itemNames[id] || id}
            disabled={disabled}
            canEdit={canEdit}
            onRemoveClick={() => onItemRemove(id)}
          />
        ))}
      </Box>
      <Box flex={true}>
        <TextInput
          size={size}
          placeholder={`Add ${
            Array.isArray(itemTypes) ? 'item' : itemTypeNames[itemType]
          }`}
          plain
          disabled={disabled || !canEdit}
          ref={textRef}
          value={value}
          onChange={e => {
            setValue(e.target.value);
          }}
          suggestions={suggestions}
          onSelect={e => {
            const { id, name } = e.suggestion.value;
            onItemAdd(id);
            const newItemNames = { ...itemNames };
            newItemNames[id] = name;
            setItemNames(newItemNames);
            setValue('');
            textRef.current.focus();
          }}
        />
      </Box>
    </Box>
  );
};

ItemTagSelector.displayName = 'ItemTagSelector';

export default ItemTagSelector;
